// invitee-highlight.ts — Pertinence d'un invité : décision pure, hors DOM.
//
// Sert deux rendus qui doivent dire la même chose :
//   - la barre « Score relatif » du panneau de proposition
//     (modules/invitation-proposal.ts) ;
//   - le soulignement des trigrammes dans l'onglet Invités d'une réunion
//     (modules/page-meeting-invitees.ts).
//
// L'échelle est RELATIVE au meilleur score de la proposition : « pertinent »
// n'a de sens que par rapport aux autres personnes du même périmètre. La cible
// (= vert) est fixée à 60 % du meilleur score, pour qu'un groupe de personnes
// comparables ressorte en vert plutôt qu'une seule.

/** Cible du score relatif : 60 % du meilleur score de la proposition. */
const TARGET_RATIO = 0.6;

export const SCORE_COLORS = {
  strong: '#22c55e',
  good: '#eab308',
  weak: '#f97316',
  none: '#dc2626',
} as const;

/** Couleur d'un score déjà normalisé (100 = cible atteinte). */
export function scoreColor(normalized: number): string {
  if (normalized >= 100) return SCORE_COLORS.strong;
  if (normalized >= 85) return SCORE_COLORS.good;
  if (normalized >= 70) return SCORE_COLORS.weak;
  return SCORE_COLORS.none;
}

/** Couleur du score relatif d'un utilisateur au sein d'une proposition.
 *
 * `maxScore` = meilleur score de la liste. À 0 (aucun point distribué), tout le
 * monde est au plus bas : personne n'a de raison identifiée d'être là. */
export function relativeScoreColor(score: number, maxScore: number): string {
  const target = maxScore * TARGET_RATIO || 1;
  return scoreColor((score / target) * 100);
}

/** Couleur de soulignement du trigramme d'un suggéré, `null` pour ne pas le
 * souligner.
 *
 * `barColor` est la couleur de SA barre de score relatif, telle que calculée au
 * rendu du panneau : les deux signaux disent alors la même chose de la même
 * personne. Deux cas sans soulignement :
 *
 * - la personne est déjà participante — son absence n'est pas une question ;
 * - sa barre est rouge — elle n'a pas de raison identifiée d'être là, donc son
 *   absence est normale et la souligner serait un faux signal.
 */
export function suggestionUnderlineColor(barColor: string, isParticipant: boolean): string | null {
  if (isParticipant || !barColor) return null;
  return barColor === SCORE_COLORS.none ? null : barColor;
}
